/**
 * @fileoverview useTheme - Theme Management Hooks
 *
 * Provides theme color management with localStorage persistence and
 * cross-tab synchronization. Supports loading themes from public assets
 * with fallback to default theme.
 *
 * Features:
 * - Loads theme from /assets/theme/colors.json
 * - Persists to localStorage for faster subsequent loads
 * - Syncs across browser tabs via storage events
 * - Provides iframe-specific variant for embedded content
 *
 * @module hooks/useTheme
 */

import { useEffect, useState } from 'react';

import { THEME_COLOR_DEFAULTS } from 'constants/themeDefaults';
import { logger } from 'utils/logger';

/**
 * Theme color configuration interface.
 * All colors should be hex values.
 *
 * @interface ThemeColors
 */
export interface ThemeColors {
  /** Background colors */
  background: {
    /** Primary background (main content areas) */
    primary: string;
    /** Secondary background (cards, panels) */
    secondary: string;
  };
  /** Text colors */
  text: {
    /** Primary text color */
    primary: string;
    /** Secondary/muted text color */
    secondary: string;
  };
  /** Brand primary color */
  primary: string;
  /** Success state color (green) */
  success: string;
  /** Warning state color (orange) */
  warning: string;
  /** Info state color (blue) */
  info: string;
  /** Error state color (red) */
  error: string;
}

const defaultTheme: ThemeColors = {
  background: {
    primary: THEME_COLOR_DEFAULTS.background.primary,
    secondary: THEME_COLOR_DEFAULTS.background.secondary,
  },
  text: {
    primary: THEME_COLOR_DEFAULTS.text.primary,
    secondary: THEME_COLOR_DEFAULTS.text.secondary,
  },
  primary: THEME_COLOR_DEFAULTS.brand,
  success: THEME_COLOR_DEFAULTS.success,
  warning: THEME_COLOR_DEFAULTS.warning,
  info: THEME_COLOR_DEFAULTS.info,
  error: THEME_COLOR_DEFAULTS.error,
};

// localStorage key
const THEME_STORAGE_KEY = 'agnostic-platform-theme';

// Save theme to localStorage
const saveThemeToStorage = (theme: ThemeColors) => {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(theme));
    logger.debug('[useTheme] Theme saved to localStorage:', { data: theme });
  } catch (error) {
    logger.error('[useTheme] Error saving theme to localStorage:', error);
  }
};

// Load theme from localStorage
const loadThemeFromStorage = (): ThemeColors | null => {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored) {
      const theme = JSON.parse(stored);
      logger.debug('[useTheme] Theme loaded from localStorage:', { data: theme });
      return theme;
    }
  } catch (error) {
    logger.error('[useTheme] Error loading theme from localStorage:', error);
  }
  return null;
};

/**
 * Hook to load and manage application theme colors.
 *
 * Loads theme from /assets/theme/colors.json on first mount,
 * caches to localStorage, and provides loading/error states.
 * Falls back to default theme if loading fails.
 *
 * @returns {Object} Theme state
 * @returns {ThemeColors} returns.theme - Current theme colors
 * @returns {boolean} returns.isLoading - Whether theme is still loading
 * @returns {Error|null} returns.error - Error if theme failed to load
 *
 * @example
 * // Using theme in a component
 * function ThemedButton() {
 *   const { theme, isLoading } = useTheme();
 *
 *   if (isLoading) return <Skeleton />;
 *
 *   return (
 *     <button
 *       style={{
 *         backgroundColor: theme.primary,
 *         color: theme.text.primary,
 *       }}
 *     >
 *       Click me
 *     </button>
 *   );
 * }
 *
 * @example
 * // Handling theme errors
 * function ThemeWrapper({ children }) {
 *   const { theme, error, isLoading } = useTheme();
 *
 *   if (error) {
 *     console.warn('Theme failed to load, using defaults');
 *   }
 *
 *   return (
 *     <div style={{ backgroundColor: theme.background.primary }}>
 *       {isLoading ? <Loading /> : children}
 *     </div>
 *   );
 * }
 */
export const useTheme = () => {
  const [theme, setTheme] = useState<ThemeColors>(defaultTheme);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const loadTheme = async () => {
      try {
        // First try loading from localStorage
        const storedTheme = loadThemeFromStorage();

        if (!storedTheme) {
          const publicTheme = '/assets/theme/colors.json';
          const response = await fetch(publicTheme);
          if (!response.ok) {
            throw new Error('Failed to load theme');
          }
          const data = await response.json();

          // Basic theme structure validation (without requiring success, warning, info, error)
          if (
            !data ||
            !data.background ||
            !data.background.primary ||
            !data.background.secondary ||
            !data.text ||
            !data.text.primary ||
            !data.text.secondary ||
            !data.primary
          ) {
            throw new Error('Invalid theme format');
          }

          // Always set fixed colors
          const newTheme = {
            ...data,
            success: THEME_COLOR_DEFAULTS.success,
            warning: THEME_COLOR_DEFAULTS.warning,
            info: THEME_COLOR_DEFAULTS.info,
            error: THEME_COLOR_DEFAULTS.error,
          };

          setTheme(newTheme);

          // Save to localStorage
          saveThemeToStorage(newTheme);

          logger.debug('[useTheme] Theme loaded from public folder and saved to localStorage');
        }
      } catch (error) {
        logger.error('[useTheme] Error loading theme:', error);
        setError(error as Error);

        // If it fails, use localStorage theme or default
        const storedTheme = loadThemeFromStorage();
        if (storedTheme) {
          setTheme(storedTheme);
          logger.debug('[useTheme] Using localStorage theme as fallback');
        } else {
          setTheme(defaultTheme);
          saveThemeToStorage(defaultTheme);
          logger.debug('[useTheme] Using default theme as fallback');
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadTheme();
  }, []);

  return {
    theme,
    isLoading,
    error,
  };
};

/**
 * Hook for iframe-embedded content to access parent theme.
 *
 * Loads theme exclusively from localStorage (no network request)
 * and listens for storage events to sync with parent app.
 * Use this in iframe content to maintain visual consistency.
 *
 * @returns {Object} Theme state
 * @returns {ThemeColors} returns.theme - Current theme colors
 * @returns {boolean} returns.isLoading - Whether theme is still loading
 *
 * @example
 * // In an iframe-embedded component
 * function IframeContent() {
 *   const { theme, isLoading } = useIframeTheme();
 *
 *   if (isLoading) return null;
 *
 *   return (
 *     <div style={{ color: theme.text.primary }}>
 *       Iframe content styled with parent theme
 *     </div>
 *   );
 * }
 *
 * @example
 * // Iframe wrapper with theme context
 * function IframeApp() {
 *   const { theme } = useIframeTheme();
 *
 *   useEffect(() => {
 *     // Apply CSS variables for iframe
 *     document.documentElement.style.setProperty('--primary', theme.primary);
 *     document.documentElement.style.setProperty('--bg-primary', theme.background.primary);
 *   }, [theme]);
 *
 *   return <IframeRoutes />;
 * }
 */
export const useIframeTheme = () => {
  const [theme, setTheme] = useState<ThemeColors>(defaultTheme);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadStoredTheme = () => {
      const storedTheme = loadThemeFromStorage();
      if (storedTheme) {
        setTheme(storedTheme);
      } else {
        setTheme(defaultTheme);
      }
      setIsLoading(false);
    };

    loadStoredTheme();

    // Listen for localStorage changes (in case the main app updates the theme)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === THEME_STORAGE_KEY && e.newValue) {
        try {
          const newTheme = JSON.parse(e.newValue);
          setTheme(newTheme);
          logger.debug('[useTheme] Theme updated via localStorage event:', { data: newTheme });
        } catch (error) {
          logger.error('[useTheme] Error processing theme change:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return {
    theme,
    isLoading,
  };
};
